import { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import {
  BlockGrid,
  Button,
  Container,
  ListItem,
  Row,
  RowColumn,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'

import ButtonLink from '~components/ButtonLink'
import Data from '~components/Data'
import Image from '~components/Image'
import { VideoModal } from '~components/Video'
import CustomerLogos from '~partials/global/CustomerLogos'
import colorfulBackground from '~media/home/colorfulBackground.svg'
import videoImage from '~media/home/videoImage.png'
import videoImageMobile from '~media/home/videoImageMobile.png'
import WhitePaperSVG from '~media/home/whitepaper.svg'
import WhitePaperMobileSVG from '~media/home/whitepapermobile.svg'
import { ReactComponent as RecreateSVG } from '~media/home/recreate.svg'
import { ReactComponent as HighlightSVG } from '~media/home/highlight.svg'
import { ReactComponent as ConfidenceSVG } from '~media/home/confidence.svg'
import { ReactComponent as CheckedSVG } from '~media/home/checked.svg'
import { ReactComponent as ShieldSVG } from '~media/home/shield.svg'
import { ReactComponent as GaugeSVG } from '~media/home/gauge.svg'
import { ReactComponent as ExperimentsSVG } from '~media/home/experiments.svg'
import { ReactComponent as PrioritizeSVG } from '~media/solutions/finserve/shift-reliability.svg'
import { ReactComponent as MeasureSVG } from '~media/product/overview/uncoverAndMeasure.svg'

const SectionIcon = ({ icon, ...rest }) => {
  let Component

  // eslint-disable-next-line default-case
  switch (icon) {
    case 'recreate':
      Component = RecreateSVG
      break
    case 'highlight':
      Component = HighlightSVG
      break
    case 'confidence':
      Component = ConfidenceSVG
      break
    case 'checked':
      Component = CheckedSVG
      break
    case 'shield':
      Component = ShieldSVG
      break
    case 'gauge':
      Component = GaugeSVG
      break
    case 'experiments':
      Component = ExperimentsSVG
      break
    case 'prioritize':
      Component = PrioritizeSVG
      break
    case 'measure':
      Component = MeasureSVG
      break
  }

  return <Component {...rest} />
}

SectionIcon.propTypes = {
  icon: PropTypes.oneOf([
    'recreate',
    'highlight',
    'confidence',
    'checked',
    'shield',
    'gauge',
    'experiments',
    'prioritize',
    'measure',
  ]),
}

export const FirstSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <div
        className="u-contrast"
        css={{
          background: theme.brand.violet,
        }}
      >
        <Section
          css={{
            [theme.mq.medium]: {
              paddingTop: '120px',
              paddingBottom: '0px',
            },
          }}
        >
          <Container size="large">
            <SectionTitle as="h2" title={<Data>{data.subHeading}</Data>} />
          </Container>
        </Section>
        <CustomerLogos
          css={{
            filter: 'brightness(0) invert(1)',
            '&': {
              [theme.mq.medium]: {
                paddingTop: '40px',
                paddingBottom: '120px',
              },
            },
          }}
        />
      </div>
      <Section
        className="u-contrast"
        css={{
          background:
            'radial-gradient(46.77% 147.55% at 69.9% 62.72%, #311173 0%, #1F1638 100%)',
          [theme.mq.medium]: {
            paddingTop: '120px',
            paddingBottom: '120px',
          },
        }}
      >
        <Container size="large">
          <Row
            gutter={{ base: 'large' }}
            css={{
              [theme.mq.medium]: { alignItems: 'center' },
            }}
          >
            <RowColumn size={{ medium: 9 }}>
              <SectionTitle
                as="h3"
                css={{
                  textAlign: 'left',
                  '.CK__SectionTitle__Header': {
                    [theme.mq.medium]: { fontSize: '58px' },
                  },
                }}
                title={<Data>{data.heading}</Data>}
              />
            </RowColumn>
            <RowColumn
              size={{ medium: 3 }}
              css={{
                textAlign: 'center',
              }}
            >
              <ButtonLink
                url={data.buttonURL}
                type="primary"
                css={{
                  marginTop: '64px',
                  [theme.mq.small__max]: {
                    marginTop: 0,
                    width: '100%',
                  },
                }}
              >
                <span>{data.buttonText}</span>
              </ButtonLink>
            </RowColumn>
          </Row>
        </Container>
      </Section>
    </Fragment>
  )
}

FirstSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    buttonURL: PropTypes.string.isRequired,
  }),
}

export const SecondSection = ({ data }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        background: theme.brand.stone100,
      }}
    >
      <Section
        css={{
          [theme.mq.medium]: {
            paddingTop: '120px',
            paddingBottom: '120px',
          },
        }}
      >
        <Container size="large">
          <Row
            gutter={{ base: 'xlarge' }}
            css={{
              [theme.mq.medium]: { alignItems: 'center' },
            }}
          >
            <RowColumn
              size={{ medium: 6 }}
              css={{
                [theme.mq.medium]: { maxWidth: '590px' },
              }}
            >
              <SectionTitle
                as="h2"
                css={{
                  marginBottom: '40px !important',
                  textAlign: 'left',
                  '.CK__SectionTitle__Sub': {
                    fontSize: '24px',
                  },
                }}
                title={<Data>{data.heading}</Data>}
                sub={<Data>{data.blurb}</Data>}
              />
              <div>
                <Button
                  type="primary"
                  url={data.buttonURL}
                  css={{
                    [theme.mq.small__max]: {
                      width: '100%',
                    },
                  }}
                >
                  {data.buttonText}
                </Button>
              </div>
            </RowColumn>
            <RowColumn
              size={{ medium: 6 }}
              css={{
                [theme.mq.medium]: { margin: '0 auto', maxWidth: '420px' },
              }}
            >
              <BlockGrid
                size={{ medium: 1 }}
                gutter={{ base: 'xlarge' }}
                css={{
                  margin: '-48px 0 0 -48px',
                  '> .CK__ListItem': {
                    paddingLeft: '48px',
                    paddingTop: '48px',
                  },
                }}
              >
                {data.items.map((item) => (
                  <ListItem key={item.icon}>
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.large,
                        alignItems: 'center',
                        textAlign: 'center',

                        [theme.mq.medium]: {
                          gridTemplateColumns: `90px 1fr`,
                          alignItems: 'start',
                          textAlign: 'left',
                        },
                      }}
                    >
                      <SectionIcon
                        css={{
                          height: 90,
                          display: 'block',
                          margin: 'auto',
                        }}
                        icon={item.icon}
                      />
                      <div
                        css={{
                          ...theme.text.xlarge,
                          alignSelf: 'center',
                        }}
                      >
                        {item.blurb}
                      </div>
                    </div>
                  </ListItem>
                ))}
              </BlockGrid>
            </RowColumn>
          </Row>
        </Container>
      </Section>
    </div>
  )
}

SecondSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
    items: PropTypes.array,
    buttonText: PropTypes.string.isRequired,
    buttonURL: PropTypes.string.isRequired,
  }),
}

export const ThirdSection = ({ data }) => {
  const theme = useTheme()

  return (
    <div>
      {data.items.map((item, index) => (
        <Section
          key={item.icon}
          css={{
            background: index % 2 !== 0 ? `url(${colorfulBackground})` : 'none',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            [theme.mq.medium]: {
              paddingTop: '120px',
              paddingBottom: '120px',
            },
          }}
        >
          <Container size="large">
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,
                marginBottom:
                  index === data.items.length - 1 ? null : theme.space.xlarge,

                [theme.mq.medium]: {
                  alignItems: 'center',
                  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                  gap: theme.space.xlarge,
                  marginBottom:
                    index === data.items.length - 1
                      ? null
                      : theme.space['2xlarge'],
                },
              }}
            >
              <div
                css={{
                  [theme.mq.medium]: {
                    gridColumn: index % 2 === 0 ? '1 / span 6' : '7 / span 6',
                    gridRow: 1,
                  },
                }}
              >
                <div
                  css={{
                    fontSize: '25px',
                    lineHeight: '1.1',
                    fontWeight: theme.fontWeight.bold,
                    marginBottom: '24px',
                  }}
                >
                  <Data>{item.subHeading}</Data>
                </div>
                <h3
                  css={{
                    marginTop: 0,
                    marginBottom: '40px',
                  }}
                >
                  <Data>{item.heading}</Data>
                </h3>
                <div css={{ fontSize: '24px' }}>
                  <Data>{item.blurb}</Data>
                </div>
              </div>
              <div
                css={{
                  [theme.mq.medium]: {
                    gridColumn: index % 2 === 0 ? '7 / span 6' : '1 / span 6',
                    gridRow: 1,
                    direction: index % 2 === 0 ? null : 'rtl',
                    order: index % 2 === 0 ? 1 : null,
                  },
                }}
              >
                <SectionIcon
                  css={{
                    [theme.mq.small__max]: {
                      maxWidth: 400,
                    },

                    [theme.mq.medium]: {
                      maxWidth: 'none',
                      maxHeight: '400px',
                      width: '100%',
                    },
                  }}
                  icon={item.icon}
                />
              </div>
            </div>
          </Container>
        </Section>
      ))}
    </div>
  )
}

ThirdSection.propTypes = {
  data: PropTypes.shape({ items: PropTypes.array }),
}

export const FourthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        background: theme.brand.stone100,
      }}
    >
      <Section
        css={{
          [theme.mq.medium]: {
            paddingTop: '120px',
            paddingBottom: '120px',
          },
        }}
      >
        <Container size="large">
          <Row
            gutter={{ base: 'xlarge' }}
            css={{
              [theme.mq.medium]: { alignItems: 'center' },
            }}
          >
            <RowColumn size={{ medium: 4 }}>
              <SectionTitle
                as="h2"
                title={<Data>{data.heading}</Data>}
                css={{
                  textAlign: 'left',
                }}
              />
            </RowColumn>
            <RowColumn size={{ medium: 8 }}>
              <BlockGrid
                size={{ medium: 3 }}
                gutter={{ base: 'large' }}
                css={{ justifyContent: 'center' }}
              >
                {data.items.map((item) => (
                  <ListItem key={item.icon}>
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.small,
                        justifyContent: 'center',
                        textAlign: 'center',
                        alignSelf: 'start',
                      }}
                    >
                      <SectionIcon
                        css={{
                          height: 90,
                          display: 'block',
                          margin: 'auto',
                          marginBottom: '33px',
                        }}
                        icon={item.icon}
                      />
                      <div
                        css={{
                          fontSize: theme.fontSize.xlarge,
                          lineHeight: '1.3',
                          fontWeight: '500',
                        }}
                      >
                        {item.blurb}
                      </div>
                    </div>
                  </ListItem>
                ))}
              </BlockGrid>
            </RowColumn>
          </Row>
        </Container>
      </Section>
    </div>
  )
}

FourthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    items: PropTypes.array,
  }),
}

const SectionVideo = ({ videoURL }) => {
  const theme = useTheme()

  const [isModalOpen, setModalOpen] = useState(false)

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <Fragment>
      <div
        tabIndex="-1"
        role="button"
        onClick={(e) => {
          e.stopPropagation()

          setModalOpen(true)
        }}
        css={{
          position: 'relative',
          cursor: 'pointer',
          boxShadow: theme.boxShadow.large,

          '&:hover, &:focus': {
            '.Home__PlayButton': {
              transform: 'translate(-50%, -50%) scale(1.125)',
            },
          },

          [theme.mq.medium]: {
            transform: `translateX(${theme.space.large}px)`,
          },
        }}
      >
        <div
          className="Home__VideoTeaser"
          css={{
            [theme.mq.medium]: {
              paddingBottom: '56.25%',
              position: 'relative',
              overflow: 'hidden',
            },

            img: {
              zIndex: 0,
            },
          }}
        >
          <Image
            src={videoImage}
            css={{
              position: 'absolute',
              top: 0,
              left: 0,
              objectFit: 'cover',

              [theme.mq.small__max]: {
                display: 'none',
              },
            }}
          />
          <Image
            src={videoImageMobile}
            css={{
              width: '100%',

              [theme.mq.medium]: {
                display: 'none',
              },
            }}
          />
        </div>
      </div>
      <VideoModal
        open={isModalOpen}
        setIsOpen={setModalOpen}
        title="Homepage video"
        src={videoURL}
      />
    </Fragment>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events */
}

SectionVideo.propTypes = {
  videoURL: PropTypes.string.isRequired,
}

export const FifthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <SectionTitle
          as="h2"
          css={{
            textAlign: 'left',

            '.CK__SectionTitle__Header': {
              marginBottom: '40px',
            },

            [theme.mq.medium]: {
              textAlign: 'center',
            },
          }}
          title={<Data>{data.heading}</Data>}
          sub={<Data>{data.blurb}</Data>}
        />
        <SectionVideo videoURL={data.videoURL} />
      </Container>
    </Section>
  )
}

FifthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
    videoURL: PropTypes.string.isRequired,
  }),
}

export const SixthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: `url(${WhitePaperSVG})`,
        backgroundColor: theme.brand.darkViolet,
        backgroundPosition: 'right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        color: theme.color.light.base,
        h2: {
          color: theme.color.light.base,
          marginTop: 0,
        },

        [theme.mq.small__max]: {
          backgroundImage: 'none',
        },
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <div
        css={{
          paddingLeft: theme.space.base,
          paddingRight: theme.space.base,
          maxWidth: StylesContainerVariables.base,
          display: 'grid',
          margin: '0 auto',
          gap: theme.space.base,
          alignItems: 'center',

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: 0,
          },
        }}
      >
        <div
          css={{
            position: 'relative',
            zIndex: 1,

            [theme.mq.medium]: {
              gridRow: 1,
              gridColumn: '1 / span 6',
            },
          }}
        >
          <div
            css={{
              fontSize: '25px',
              lineHeight: '1.1',
              fontWeight: theme.fontWeight.bold,
              marginBottom: '24px',
            }}
          >
            <Data>{data.sectionType}</Data>
          </div>
          <h2
            css={{
              marginBottom: '40px',
              [theme.mq.medium]: {
                fontSize: '60px',
              },
            }}
          >
            <Data>{data.heading}</Data>
          </h2>
          <div css={{ fontSize: '24px', marginBottom: '40px' }}>
            <Data>{data.blurb}</Data>
          </div>
          <ButtonLink
            css={{
              marginTop: theme.space.large,
              [theme.mq.small__max]: {
                width: '100%',
              },
            }}
            type="primary"
            url={data.buttonURL}
          >
            {data.buttonText}
          </ButtonLink>
        </div>
      </div>
      <div
        css={{
          marginTop: theme.space.large,
        }}
      >
        <Image
          src={WhitePaperMobileSVG}
          css={{
            [theme.mq.medium]: {
              display: 'none',
            },
          }}
        />
      </div>
    </Section>
  )
}

SixthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
    sectionType: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    buttonURL: PropTypes.string.isRequired,
    sectionImage: PropTypes.string.isRequired,
  }),
}
